import type {ReactElement, RefObject} from 'react';
import React from 'react';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import styles from './base-timestamps.module.scss';
import {useAppSelector} from '@/hooks';

interface BaseTimestampsProps {
  widthPerSecond: number;
  duration: number;
  stepSize: number;
  timelineContainerRef: RefObject<HTMLDivElement | null>;
  className?: string;
}

export function BaseTimestamps({className = '', ...props}: BaseTimestampsProps): ReactElement<any> | null {
  const widthPerSecond = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.widthPerSecond;
  });
  const isPanning = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isPanning;
  });
  const isDesignItemResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isDesignResizing;
  });

  const getValueStructure = (val: number, offset = 1): ReactElement<any> => {
    return (
      <div
        key={`container-${val}`}
        className={`${styles.secondsContainer} _full-height`}
        style={{
          width: `${widthPerSecond * offset}px`,
        }}
      >
        <span className={`${styles.nonDottedTimestamp} ${styles.customSmallFont} ${String(val).length === 1 ? styles.smallRight : styles.largeRight}`} key={`${val}`}>
          {val}s
        </span>
      </div>
    );
  };

  const getDottedStructure = (val: number, offset = 1): ReactElement<any> => {
    return (
      <div
        key={`dot-container-${val}`}
        className={`${styles.secondsContainer} ${isPanning || isDesignItemResizing ? '' : styles.transition} _full-height`}
        style={{
          width: `${widthPerSecond * offset}px`,
        }}
      >
        <div className={`${styles.dotTimestamp} radius-round`} key={`${val}`} />
      </div>
    );
  };

  const prepareSingleTimeStamp = (val: number): Array<ReactElement<any>> => {
    const elements: Array<ReactElement<any>> = [];

    if (val % props.stepSize === 0) {
      elements.push(getValueStructure(val));
    } else {
      elements.push(getDottedStructure(val));
    }

    return elements;
  };

  const getStepSizeForDots = (): number => {
    if (widthPerSecond > 40) {
      return 1;
    }

    if (widthPerSecond < 20) {
      return 15;
    }

    return 5;
  };

  const prepareTimestamps = (): Array<ReactElement<any>> => {
    const elements: Array<ReactElement<any>> = [];
    elements.push(
      <span className={`${styles.zero} ${isEditorMobileVariant() ? styles.mobile : ''} ${styles.customSmallFont}`} key={0}>
        0s
      </span>
    );

    if (props.stepSize === 0.5) {
      for (let i = 0.5; i <= Math.ceil(props.duration); i += props.stepSize) {
        elements.push(getValueStructure(i));
      }
      return elements;
    }

    const stepSizeForDots = Math.round(getStepSizeForDots());
    if (stepSizeForDots === 1) {
      for (let i = 1; i <= Math.ceil(props.duration); i++) {
        elements.push(...prepareSingleTimeStamp(i));
      }
      return elements;
    }

    for (let i = 1; i <= Math.ceil(props.duration); i += 1) {
      if (i % props.stepSize === 0) {
        elements.push(getValueStructure(i, stepSizeForDots));
      }

      if (i % props.stepSize !== 0 && i % stepSizeForDots === 0) {
        elements.push(getDottedStructure(i, stepSizeForDots));
      }
    }

    return elements;
  };

  return <div className={`${styles.container} _full-width _full-height ${className} flex-h-row flex-items-center`}>{prepareTimestamps()}</div>;
}
